import React, { useState } from 'react';
import { X, Edit3, ChevronDown } from 'lucide-react';

const DataPromptHelper = ({ missingData, onEdit, onDismiss, readOnly = false }) => {
  const [expanded, setExpanded] = useState(false);
  
  if (!missingData || missingData === 'none') return null;

  const getMessage = () => {
    switch (missingData) {
      case 'universe':
        return {
          title: '💡 Want deeper insights?',
          subtitle: "It looks like you haven't included much about signs or synchronicities.",
          content: (
            <>
              <p className="font-semibold mb-2">Here are some things to notice:</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>Have you seen any recurring patterns lately?</li>
                <li>Does the timing of this decision feel significant?</li>
                <li>Have you had dreams related to this?</li>
                <li>Do certain symbols or words keep appearing?</li>
                <li>What "coincidences" have you noticed?</li>
              </ul>
              <p className="mt-3 text-sm">
                Edit your prompt above to include these, and I can provide richer insights about what the universe might be showing you.
              </p>
            </>
          )
        };
      
      case 'vibe':
        return {
          title: '💡 Want deeper insights?',
          subtitle: "It looks like you haven't included much about how each option FEELS in your body.",
          content: (
            <>
              <p className="font-semibold mb-2">Here are some things to notice:</p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>When you imagine Option A, does your body relax or tense up?</li>
                <li>Does thinking about it create excitement or dread?</li>
                <li>Do you feel expanded or contracted?</li>
                <li>Light and energized, or heavy and drained?</li>
              </ul>
              <p className="mt-3 text-sm">
                Edit your prompt above to include how each option feels, and I can provide richer insights about your body's wisdom.
              </p>
            </>
          )
        };
      
      case 'both':
        return {
          title: '💡 Want deeper insights?',
          subtitle: "I see you've shared the logical side - great start!",
          content: (
            <>
              <p className="mb-2">To get the most from DECIDE, also consider:</p>
              <div className="space-y-2">
                <div>
                  <p className="font-semibold">Signs from the Universe:</p>
                  <p className="text-sm">What patterns, timing, or "coincidences" have you noticed?</p>
                </div>
                <div>
                  <p className="font-semibold">Wisdom from Your Body:</p>
                  <p className="text-sm">How does each option FEEL physically when you think about it?</p>
                </div>
              </div>
              <p className="mt-3 text-sm">
                Edit your prompt above to include these, and I'll provide much richer insights.
              </p>
            </>
          )
        };
      
      default:
        return null;
    }
  };

  const message = getMessage();
  if (!message) return null;

  // Collapsed state - just show title with expand arrow
  if (!expanded) {
    return (
      <div className="p-4 my-6 bg-purple-50 rounded-xl shadow-md">
        <button
          onClick={() => setExpanded(true)}
          className="w-full flex items-center justify-between text-left hover:bg-purple-100 transition-colors rounded-lg p-2"
        >
          <h3 className="text-lg font-bold text-purple-900">{message.title}</h3>
          <ChevronDown className="w-5 h-5 text-purple-600" />
        </button>
      </div>
    );
  }

  // Expanded state - show full content with X to close
  return (
    <div className="p-6 my-6 bg-purple-50 rounded-xl shadow-md relative">
      <button
        onClick={() => setExpanded(false)}
        className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
        aria-label="Close"
      >
        <X className="w-5 h-5" />
      </button>
      
      <h3 className="text-xl font-bold text-purple-900 mb-2">{message.title}</h3>
      <p className="text-gray-700 mb-4">{message.subtitle}</p>
      
      <div className="text-gray-700 mb-4">
        {message.content}
      </div>
      
      {!readOnly && onEdit && (
        <button
          onClick={onEdit}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
        >
          <Edit3 className="w-4 h-4" />
          <span>Edit My Prompt</span>
        </button>
      )}
    </div>
  );
};

export default DataPromptHelper;
